import { Link as RouterLink } from 'react-router';
import { Button, Stack, Typography, useTheme } from '@mui/material';
import { VideocamOutlined } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import { agentPoolsTabPath } from '@/components/agent-pools-admin/agent-pools-tabs';
import OpsDomainPanel from './ops-domain-panel';
import OpsInlineBar from './ops-inline-bar';
import OpsStackedBar from './ops-stacked-bar';
import { formatDuration } from './ops-format';
import type { OpsSingleCamExhaustionDto, ProcessorOpsHealthStatus } from '@/types/dtos/ops/ops-platform.dto';

type SingleCamDomainPanelProps = {
  exhaustion: OpsSingleCamExhaustionDto;
  health: ProcessorOpsHealthStatus | null;
};

export default function SingleCamDomainPanel({ exhaustion, health }: SingleCamDomainPanelProps) {
  const t = useTranslations();
  const theme = useTheme();

  const headline = exhaustion.poolEmptyWithBacklog
    ? t(
        'admin.opsAdmin.singlecamHeadline.poolEmptyWithBacklog',
        {
          totalBacklog: exhaustion.totalBacklog,
          oldest: formatDuration(exhaustion.oldestQueuedAgeSeconds),
        },
        `No free — ${exhaustion.totalBacklog} queued (oldest ${formatDuration(exhaustion.oldestQueuedAgeSeconds)})`,
      )
    : exhaustion.allocatedOfflineWhileLiveSignal
      ? t(
          'admin.opsAdmin.singlecamHeadline.allocatedOfflineWhileLive',
          {
            offline: exhaustion.allocatedOfflineWhileLive,
            allocated: exhaustion.allocated,
          },
          `${exhaustion.allocatedOfflineWhileLive} allocated offline while live`,
        )
      : exhaustion.queueStuckWithFreeAgent
        ? t(
            'admin.opsAdmin.singlecamHeadline.queueStuckWithFreeAgent',
            { totalBacklog: exhaustion.totalBacklog, free: exhaustion.free },
            `Queue stuck — ${exhaustion.totalBacklog} queued, ${exhaustion.free} free`,
          )
        : exhaustion.poolExhausted
          ? t(
              'admin.opsAdmin.singlecamHeadline.poolExhausted',
              { allocated: exhaustion.allocated, enrolled: exhaustion.enrolled },
              `${exhaustion.allocated}/${exhaustion.enrolled} allocated`,
            )
          : undefined;

  const queueMax = Math.max(exhaustion.enrolled, exhaustion.totalBacklog, 1);

  return (
    <OpsDomainPanel
      title={t('admin.opsAdmin.singlecamMetricsTitle', undefined, 'Single-cam agent pool')}
      icon={<VideocamOutlined fontSize="small" color="action" />}
      health={health}
      headline={headline}
      footer={
        <Button
          size="small"
          component={RouterLink}
          to={agentPoolsTabPath('singlecam')}
          sx={{ fontSize: '0.7rem', py: 0 }}
        >
          {t('admin.opsAdmin.openSingleCamPool', undefined, 'Open pool')}
        </Button>
      }
    >
      <OpsStackedBar
        label={t('admin.opsAdmin.metrics.enrolled', undefined, 'Enrolled')}
        total={exhaustion.enrolled}
        segments={[
          { key: 'allocated', value: exhaustion.allocated, color: theme.palette.warning.main, label: 'Alloc' },
          { key: 'free', value: exhaustion.free, color: theme.palette.primary.main, label: 'Free' },
          { key: 'maint', value: exhaustion.maintenance, color: theme.palette.action.disabled, label: 'Maint' },
        ]}
      />
      <OpsInlineBar
        label={t('admin.opsAdmin.metrics.queued', undefined, 'Queued')}
        value={exhaustion.totalBacklog}
        max={queueMax}
        warnThreshold={1}
      />
      <Stack direction="row" spacing={1.5} flexWrap="wrap">
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
          {t('admin.opsAdmin.metrics.flowsWithBacklog', undefined, 'Flows w/ backlog')} {exhaustion.flowsWithBacklog}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
          {t('admin.opsAdmin.metrics.oldestQueue', undefined, 'Oldest')}{' '}
          {formatDuration(exhaustion.oldestQueuedAgeSeconds)}
        </Typography>
        {exhaustion.allocated > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
            {t('admin.opsAdmin.metrics.allocatedConnected', undefined, 'Alloc connected')}{' '}
            {exhaustion.allocatedConnected}/{exhaustion.allocated}
          </Typography>
        )}
      </Stack>
    </OpsDomainPanel>
  );
}
